/* ================= App lock: Face ID / Touch ID / fingerprint / Windows Hello =================
   Uses the device's own unlock (WebAuthn platform authenticator, user verification required).
   Plume never sees a face or fingerprint: the phone only answers "the owner confirmed".
   The lock is per device and per user. If the unlock fails or the passkey is gone, the way out
   is a fresh sign-in with an email code, so the lock is never weaker than the account itself. */
import { CLOUD, loggedIn } from './api.js';
import { t } from '../i18n/index.js';
import { esc, toast } from '../ui.js';

const KEY='plume.lock',SEEN='plume.lockseen';
export const LOCK={avail:null,locked:false,busy:false,graceUntil:0};
export const LOCK_AFTER=[0,1,5,15];

function cfg(){try{return JSON.parse(localStorage.getItem(KEY))||null}catch(e){return null}}
function saveCfg(c){try{if(c)localStorage.setItem(KEY,JSON.stringify(c));else localStorage.removeItem(KEY)}catch(e){}}
function uid(){return (CLOUD.auth&&CLOUD.auth.uid)||''}
/* the lock for the person signed in here, or null */
export function lockCfg(){const c=cfg();return c&&c.uid&&c.uid===uid()&&c.id?c:null}
export function lockOn(){return loggedIn()&&!!lockCfg()}

const b64u=buf=>btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'');
const unb64u=s=>Uint8Array.from(atob(s.replace(/-/g,'+').replace(/_/g,'/')+'==='.slice((s.length+3)%4)),c=>c.charCodeAt(0));
const rand=n=>crypto.getRandomValues(new Uint8Array(n));

/* the name people know their device unlock by */
export function bioName(){
  const ua=navigator.userAgent||'';
  if(/iPhone|iPad|iPod/.test(ua)||(/Macintosh/.test(ua)&&navigator.maxTouchPoints>1))return 'Face ID / Touch ID';
  if(/Macintosh/.test(ua))return 'Touch ID';
  if(/Android/.test(ua))return t('lock.android');
  if(/Windows/.test(ua))return 'Windows Hello';
  return t('lock.generic');
}
export async function lockAvailable(){
  if(LOCK.avail!==null)return LOCK.avail;
  try{LOCK.avail=!!(window.PublicKeyCredential&&window.isSecureContext&&await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())}
  catch(e){LOCK.avail=false}
  return LOCK.avail;
}

/* the authenticator data must say the person was verified (UV flag), not just present */
function verified(resp,challenge){
  try{
    const cd=JSON.parse(new TextDecoder().decode(resp.clientDataJSON));
    if(cd.type!=='webauthn.get'||cd.challenge!==b64u(challenge)||cd.origin!==location.origin)return false;
    const ad=new Uint8Array(resp.authenticatorData);
    return (ad[32]&0x05)===0x05;   // UP + UV
  }catch(e){return false}
}

/* ---------- set up / switch off (Settings → Konto & Sicherheit) ---------- */
/* Safari only allows the passkey prompt straight out of a tap: nothing may be awaited before
   navigator.credentials.create(), or iOS refuses silently. So this starts synchronously. */
export function enableLock(){
  if(!loggedIn())return Promise.resolve(false);
  if(LOCK.avail===false||!window.PublicKeyCredential||!navigator.credentials){toast(t('lock.na'));return Promise.resolve(false)}
  const a=CLOUD.auth;
  let p;
  try{
    p=navigator.credentials.create({publicKey:{
      rp:{name:'Plume',id:location.hostname},
      user:{id:rand(16),name:a.email||'Plume',displayName:a.name||a.email||'Plume'},
      challenge:rand(32),
      pubKeyCredParams:[{type:'public-key',alg:-7},{type:'public-key',alg:-257}],
      authenticatorSelection:{authenticatorAttachment:'platform',userVerification:'required',residentKey:'preferred'},
      timeout:60000,attestation:'none'
    }});
  }catch(e){p=Promise.reject(e)}
  return p.then(cred=>{
    if(!cred)throw new Error('no credential');
    const old=lockCfg();
    saveCfg({uid:a.uid,id:b64u(cred.rawId),after:old?old.after:1,at:Date.now()});
    markSeen();
    return true;
  }).catch(e=>{
    if(!e||e.name!=='AbortError')toast(e&&e.name==='NotAllowedError'?t('lock.eCancel'):t('lock.eSetup')+(e&&e.name?' ('+e.name+')':''));
    return false;
  });
}
export function disableLock(){saveCfg(null);unlockUI()}
export function setLockAfter(min){const c=lockCfg();if(!c)return;c.after=+min||0;saveCfg(c)}

/* ---------- the lock screen ---------- */
const FACE='<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M6 16V11a5 5 0 0 1 5-5h5M32 6h5a5 5 0 0 1 5 5v5M42 32v5a5 5 0 0 1-5 5h-5M16 42h-5a5 5 0 0 1-5-5v-5"/><path d="M17 18v3M31 18v3M24 18v9h-2.5M18.5 32.5c3.4 2.6 7.6 2.6 11 0"/></svg>';
const PRINT='<svg viewBox="0 0 48 48" aria-hidden="true"><path d="M13.5 13a15 15 0 0 1 25 11.5c0 3-.3 5.7-.9 8.3"/><path d="M9.5 20.5a15 15 0 0 0-.5 4c0 4.5-.7 8-2 10.5"/><path d="M17.5 24.5a6.5 6.5 0 0 1 13 0c0 6.5-1.2 11.6-3.6 15.5"/><path d="M24 24.5c0 7-1.6 12.6-4.8 16.5"/><path d="M11.6 39.5c2.6-3.6 4.1-8.7 4.4-15"/><path d="M33.3 39.8c.7-1.4 1.3-3 1.8-4.6"/></svg>';
function glyph(){return /Face ID|Windows Hello/.test(bioName())?FACE:PRINT}

function root(){
  let r=document.getElementById('lockroot');
  if(!r){r=document.createElement('div');r.id='lockroot';document.body.appendChild(r)}
  return r;
}
function lockUI(){
  LOCK.locked=true;
  document.documentElement.classList.add('locked');
  const a=CLOUD.auth||{},name=bioName();
  root().innerHTML=`<div class="lockback" role="dialog" aria-modal="true" aria-labelledby="lk-t">
    <div class="lockcard">
      <div class="label lk-label">
        <div class="lb-mark">plume<i>.</i></div>
        <div class="lb-line"><span>${esc(t('lock.name'))}</span><b>${esc(a.name||a.email||'')}</b></div>
      </div>
      <h1 id="lk-t" class="lk-title">${esc(t('lock.locked'))}</h1>
      <button type="button" class="lk-bio" data-lk="go" aria-label="${esc(t('lock.unlockWith',{m:name}))}">${glyph()}</button>
      <button type="button" class="lk-go" data-lk="go">${esc(t('lock.unlockWith',{m:name}))}</button>
      <p class="lk-msg" aria-live="polite"></p>
      <button type="button" class="lk-alt" data-lk="alt">${esc(t('lock.fallback'))}</button>
    </div></div>`;
  root().querySelectorAll('[data-lk=go]').forEach(b=>b.onclick=()=>unlock(true));
  root().querySelector('[data-lk=alt]').onclick=fallback;
}
function unlockUI(){
  LOCK.locked=false;
  document.documentElement.classList.remove('locked');
  const r=document.getElementById('lockroot');if(r)r.innerHTML='';
}
function msg(s){const m=document.querySelector('#lockroot .lk-msg');if(m)m.textContent=s||''}

let pending=null;
export function unlock(userTap){
  const c=lockCfg();
  if(!c){unlockUI();return Promise.resolve(true)}
  /* a tap always wins: drop a prompt that started without one (iOS ignores those) */
  if(pending){if(!userTap)return Promise.resolve(false);try{pending.abort()}catch(e){}}
  const ctl=new AbortController();pending=ctl;LOCK.busy=true;msg('');
  const bio=document.querySelector('#lockroot .lk-bio');if(bio)bio.classList.add('busy');
  const challenge=rand(32);
  let p;
  try{
    p=navigator.credentials.get({signal:ctl.signal,publicKey:{
      challenge,rpId:location.hostname,timeout:60000,userVerification:'required',
      allowCredentials:[{type:'public-key',id:unb64u(c.id),transports:['internal','hybrid']}]
    }});
  }catch(e){p=Promise.reject(e)}
  return p.then(res=>{
    if(!res||!verified(res.response,challenge))throw Object.assign(new Error('not verified'),{name:'NotVerified'});
    markSeen();unlockUI();return true;
  }).catch(e=>{
    if(userTap&&!(e&&e.name==='AbortError'))msg(t('lock.failed'));
    return false;
  }).finally(()=>{
    if(pending===ctl){pending=null;LOCK.busy=false;const b=document.querySelector('#lockroot .lk-bio');if(b)b.classList.remove('busy')}
  });
}
const IOS=/iPhone|iPad|iPod/.test(navigator.userAgent||'')||(/Macintosh/.test(navigator.userAgent||'')&&navigator.maxTouchPoints>1);
/* the way out: sign in again with an email code. The lock stays on for next time. */
function fallback(){
  const go=async()=>{
    try{if(CLOUD.dirty){const s=await import('./sync.js');await s.cloudPush()}}catch(e){}
    const {cloudSignOut}=await import('./auth.js');
    unlockUI();cloudSignOut();
  };
  const r=root(),card=r.querySelector('.lockcard');if(!card)return go();
  const p=document.createElement('div');p.className='lk-confirm';
  p.innerHTML=`<p>${esc(t('lock.fallbackAsk'))}</p><div class="row"><button type="button" class="btn" data-n>${esc(t('c.cancel'))}</button><button type="button" class="btn primary" data-y>${esc(t('lock.fallbackGo'))}</button></div>`;
  card.querySelector('.lk-alt').replaceWith(p);
  p.querySelector('[data-n]').onclick=()=>lockUI();
  p.querySelector('[data-y]').onclick=go;
}

/* ---------- when to lock ---------- */
function markSeen(){try{localStorage.setItem(SEEN,String(Date.now()))}catch(e){}}
function due(){
  const c=lockCfg();if(!c)return false;
  if(Date.now()<LOCK.graceUntil)return false;
  const seen=+(localStorage.getItem(SEEN)||0);
  return !seen||Date.now()-seen>=(c.after||0)*60000;
}
/* picking a photo or sharing a recap leaves the app for a moment; that should not lock it */
function grace(ms){LOCK.graceUntil=Date.now()+ms}
/* called once, before the first render with data */
export function initLock(){
  lockAvailable();
  if(lockOn()&&due()){lockUI();if(!IOS)setTimeout(()=>unlock(false),300)}
  document.addEventListener('click',e=>{if(e.target.closest&&e.target.closest('input[type=file],a[href^="mailto:"],a[href^="tel:"],a[href*="wa.me"]'))grace(5*60000)},true);
  if(navigator.share){const s=navigator.share.bind(navigator);navigator.share=(...a)=>{grace(5*60000);return s(...a)}}
  document.addEventListener('visibilitychange',()=>{
    if(!lockOn())return;
    if(document.hidden){
      if(LOCK.locked)return;
      markSeen();
      /* "Sofort": cover the app before the phone takes its app-switcher snapshot */
      const c=lockCfg();if(c&&!c.after&&Date.now()>=LOCK.graceUntil)lockUI();
    }else{
      if(!LOCK.locked&&due())lockUI();
      LOCK.graceUntil=0;
      if(LOCK.locked&&!IOS)setTimeout(()=>unlock(false),250);
    }
  });
  window.addEventListener('pagehide',()=>{if(lockOn()&&!LOCK.locked)markSeen()});
}
